import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';

const LIMIT_OPTIONS = [10, 20, 50];
const MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const BAR_COLORS = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#DC2626', '#0891B2', '#DB2777', '#4F46E5'];

const fmt = (n, digits = 0) =>
  (Number(n) || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const parseTopMaintenanceResponse = (response) => {
  if (Array.isArray(response?.items)) return response;
  if (Array.isArray(response?.data?.items)) return response.data;
  return response?.data || response || { items: [] };
};

const truncateLabel = (text, max = 28) => {
  const value = String(text || '').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
};

function RankTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="tm-chart-tooltip">
      <div className="tm-chart-tooltip-title">{row.fullName}</div>
      <div className="tm-chart-tooltip-row">
        <span className="dot" style={{ background: row.fill }} />
        <span>Bakım kaydı</span>
        <span className="val">{fmt(row.maintenance_count)}</span>
      </div>
      {row.area && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Alan: {row.area}</div>
      )}
    </div>
  );
}

const TopMaintenanceEquipmentPanel = ({ currentYear, yearOptions }) => {
  const [limit, setLimit] = useState(10);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const hasDateFilter = Boolean(year || month || dateFrom || dateTo);

  const clearDateFilters = () => {
    setYear('');
    setMonth('');
    setDateFrom('');
    setDateTo('');
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { limit: String(limit) };
        if (year) params.year = year;
        if (month) params.month = month;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;

        const response = await technicalMaintenanceApi.getTopMaintenanceEquipment(params);
        const parsed = parseTopMaintenanceResponse(response);
        setItems(parsed.items || []);
        setMeta(parsed);
      } catch (err) {
        setError(err.message || 'Ekipman sıralaması yüklenemedi.');
        setItems([]);
        setMeta(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [limit, year, month, dateFrom, dateTo]);

  const chartData = useMemo(() => {
    const maxCount = items.reduce((max, item) => Math.max(max, item.maintenance_count || 0), 0) || 1;
    return items.map((item, index) => ({
      ...item,
      fullName: item.name,
      chartLabel: truncateLabel(item.equipment_code ? `${item.name} (${item.equipment_code})` : item.name, 24),
      fill: BAR_COLORS[index % BAR_COLORS.length],
      share: ((item.maintenance_count || 0) / maxCount) * 100,
    }));
  }, [items]);

  const totalMaintenance = meta?.total_records_in_period ?? 0;
  const chartHeight = Math.max(220, Math.min(520, chartData.length * 30 + 40));

  const periodLabel = hasDateFilter
    ? [
        year || null,
        month ? MONTH_SHORT[Number(month) - 1] : (year ? 'Tüm yıl' : null),
        dateFrom || dateTo ? `${dateFrom || '…'} – ${dateTo || '…'}` : null,
      ].filter(Boolean).join(' · ')
    : 'Tüm zamanlar';

  return (
    <div className="tm-dash-card">
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="!mb-1">En Çok Bakım Kaydı Açılan Ekipmanlar</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Bakım kaydı açılma sayısına göre sıralama — dönem seçmezseniz tüm zamanlar gösterilir
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{periodLabel}</p>
          </div>
          <div className="tm-limit-chips">
            {LIMIT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`tm-month-chip${limit === option ? ' active' : ''}`}
                onClick={() => setLimit(option)}
              >
                Top {option}
              </button>
            ))}
          </div>
        </div>

        <div className="tm-dash-toolbar pb-4 border-b border-gray-100 dark:border-gray-800">
          <button
            type="button"
            className={`tm-month-chip${!hasDateFilter ? ' active' : ''}`}
            onClick={clearDateFilters}
          >
            Tüm zamanlar
          </button>
          <div className="tm-filter-group">
            <span className="tm-filter-group-label">Yıl</span>
            <select
              className="tm-filter-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">—</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="tm-month-chips">
            {MONTH_SHORT.map((label, index) => {
              const monthValue = String(index + 1);
              const active = month === monthValue;
              return (
                <button
                  key={label}
                  type="button"
                  className={`tm-month-chip${active ? ' active' : ''}`}
                  disabled={!year}
                  onClick={() => setMonth((prev) => (prev === monthValue ? '' : monthValue))}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="tm-filter-group">
            <span className="tm-filter-group-label">Başlangıç</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="tm-date-input"
            />
          </div>
          <div className="tm-filter-group">
            <span className="tm-filter-group-label">Bitiş</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="tm-date-input"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-[220px] text-blue-600 dark:text-blue-400 animate-pulse">
          Yükleniyor...
        </div>
      ) : items.length === 0 ? (
        <div className="tm-dash-empty">Seçilen dönem için bakım kaydı bulunamadı.</div>
      ) : (
        <>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Dönemdeki toplam bakım kaydı:{' '}
            <span className="font-semibold text-gray-700 dark:text-gray-300">{fmt(totalMaintenance)}</span>
          </div>

          <div style={{ width: '100%', height: chartHeight }} className="mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="chartLabel"
                  width={130}
                  tick={{ fontSize: 10, fill: '#64748B' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  tickLine={false}
                />
                <Tooltip content={<RankTooltip />} cursor={{ fill: 'rgba(37, 99, 235, 0.08)' }} />
                <Bar dataKey="maintenance_count" name="Bakım" radius={[0, 6, 6, 0]} maxBarSize={18}>
                  {chartData.map((entry) => (
                    <Cell key={entry.equipment_id} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3 font-medium w-10">#</th>
                  <th className="py-2 pr-3 font-medium">Makine</th>
                  <th className="py-2 pr-3 font-medium">Alan</th>
                  <th className="py-2 pr-3 font-medium text-right">Kayıt</th>
                  <th className="py-2 font-medium">Yoğunluk</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr key={item.equipment_id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2.5 pr-3 text-gray-400 font-semibold">{index + 1}</td>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {[item.equipment_code, item.category_label].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">{item.area || '—'}</td>
                    <td className="py-2.5 pr-3 text-right font-semibold text-gray-900 dark:text-white">
                      {fmt(item.maintenance_count)}
                    </td>
                    <td className="py-2.5">
                      <div className="tm-share-cell">
                        <span className="tm-share-bar">
                          <span style={{ width: `${Math.min(100, item.share)}%`, background: item.fill }} />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TopMaintenanceEquipmentPanel;
